import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { CampoData, Rotulo, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { AvisoExportacao } from '../../componentes/discreto';
import { get, mensagemDeErro, post, textoDeErroGravado } from '../../nucleo/api';
import { useContas } from './dados';
import { hojeLocal } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';

interface Job {
  estado: 'pendente' | 'rodando' | 'concluido' | 'erro';
  progresso: number;
  resultado: { anexo_id: string; nome: string; linhas: number } | null;
  erro: string | null;
}

/** M4.6 ExportarContabil: CSV do período gerado por job; o resultado traz o anexo para baixar. */
export function ExportarContabil({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const contas = useContas();
  const hoje = hojeLocal();
  const [de, setDe] = useState(`${hoje.slice(0, 7)}-01`);
  const [ate, setAte] = useState(hoje);
  const [conta, setConta] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const [enviando, setEnviando] = useState(false);
  const job = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => get<Job>(`/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && ['concluido', 'erro'].includes(q.state.data.estado) ? false : 1000),
  });
  const pronto = job.data?.estado === 'concluido' && job.data.resultado;
  const fechar = () => {
    setJobId(null);
    setErro(null);
    aoFechar();
  };
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('exportar.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {jobId ? tn('comum.fechar') : tn('comum.cancelar')}
          </Botao>
          {!jobId && (
            <Botao
              variante="primario"
              carregando={enviando}
              disabled={!de || !ate}
              onClick={async () => {
                setEnviando(true);
                setErro(null);
                try {
                  setJobId((await post<{ job_id: string }>('/financeiro/exportacoes/contabil', { de, ate, conta_id: conta || undefined })).job_id);
                } catch (e) {
                  setErro(mensagemDeErro(e));
                } finally {
                  setEnviando(false);
                }
              }}
            >
              {t('exportar.gerar')}
            </Botao>
          )}
        </>
      }
    >
      <AvisoExportacao />
      <p className="mb-4 text-sm text-tinta-2">{t('exportar.texto')}</p>
      {erro && (
        <p role="alert" className="mb-3 text-sm text-perigo">
          {erro}
        </p>
      )}
      {!jobId ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Rotulo id="exp-de" rotulo={t('filtros.de')}>
            <CampoData id="exp-de" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
          </Rotulo>
          <Rotulo id="exp-ate" rotulo={t('filtros.ate')}>
            <CampoData id="exp-ate" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
          </Rotulo>
          <div className="sm:col-span-2">
            <Rotulo id="exp-conta" rotulo={t('campos.conta')}>
              <Selecao id="exp-conta" vazio={t('filtros.todasContas')} opcoes={(contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} value={conta} onChange={(e) => setConta(e.target.value)} />
            </Rotulo>
          </div>
        </div>
      ) : (
        <div role="status" className="rounded-lg border border-borda p-4">
          {!pronto && job.data?.estado !== 'erro' && (
            <>
              <p className="text-sm">{t(job.data?.estado === 'pendente' ? 'exportar.naFila' : 'exportar.andamento')}</p>
              <div className="mt-2 h-2 w-full rounded-full bg-sup-3">
                <div className={`h-2 rounded-full bg-acento-forte transition-all ${job.data?.estado === 'pendente' ? 'animate-pulse' : ''}`} style={{ width: `${job.data?.estado === 'pendente' ? 5 : (job.data?.progresso ?? 5)}%` }} />
              </div>
            </>
          )}
          {job.data?.estado === 'erro' && <p className="text-sm text-perigo">{textoDeErroGravado(job.data.erro)}</p>}
          {pronto && job.data?.resultado && (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium">{t('exportar.pronto', { count: job.data.resultado.linhas })}</p>
              <a href={`/api/v1/anexos/${job.data.resultado.anexo_id}`} download className="inline-flex w-fit items-center gap-2 rounded-lg border border-borda px-4 py-2 text-sm font-medium text-acento-forte hover:bg-sup-2">
                <Download className="size-4" aria-hidden />
                {job.data.resultado.nome}
              </a>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
